import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import WhyCarlos from "@/components/landing/WhyCarlos";
import FinalCTA from "@/components/landing/FinalCTA";
import { Check, Layers, Users, BarChart3 } from "lucide-react";

const OKR_ITEMS = [
  { title: "Claridad de objetivos", text: "OKR ayuda a definir qué quieres lograr y cómo medirlo. Crea foco en metas concretas y resultados medibles." },
  { title: "Cultura de medición", text: "Instala el hábito de rastrear resultados clave y genera accountability en todos los niveles del equipo." },
  { title: "Ampliamente validado", text: "Google, Intel y cientos de empresas tech lo utilizan como marco principal de definición de metas con excelentes resultados." },
];

const EOS_ITEMS = [
  { title: "Ordena el sistema completo", text: "EOS va más allá de los objetivos: estructura roles, procesos, cultura y la cohesión del equipo directivo en un solo marco." },
  { title: "Salud organizacional", text: "Ninguna empresa puede escalar si el equipo de arriba no funciona como equipo. EOS trabaja explícitamente la cohesión del liderazgo." },
  { title: "Cadencia de ejecución integrada", text: "La Reunión Nivel 10 (L10) garantiza accountability semanal sin agregar reuniones adicionales. El ritmo está incorporado al sistema." },
];

const DIFFERENTIATORS = [
  {
    icon: Layers,
    num: "01",
    title: "Más allá de los objetivos",
    text: "OKR define el qué. EOS define el qué, el cómo, el quién y el cuándo — y alinea al equipo alrededor de una Visión a 10 años que da sentido a cada prioridad trimestral.",
  },
  {
    icon: Users,
    num: "02",
    title: "Personas correctas, roles correctos",
    text: "El Accountability Chart de EOS asegura que cada función tiene un responsable claro y que las personas correctas ocupan los lugares correctos. Sin zonas grises.",
  },
  {
    icon: BarChart3,
    num: "03",
    title: "Ejecución trimestral estructurada",
    text: "Los Rocks Trimestrales de EOS traducen la visión en prioridades de 90 días con responsable y fecha. Más concreto que un OKR trimestral sin cadencia de seguimiento.",
  },
];

export default function EOSvsOKR() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "EOS vs OKR: ¿Cuál es mejor para tu empresa? | Carlos Garcín Implementador EOS Chile",
      description: "EOS vs OKR: compara ambos enfoques y descubre por qué EOS es más completo para escalar tu empresa. Carlos Garcín, Implementador EOS oficial en Chile.",
      path: "/eos-vs-okr",
      keywords: "EOS vs OKR, diferencia EOS y OKR, metodologías de gestión empresarial, OKR o EOS, Rocks EOS",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="EOS vs OKR — Comparativa de metodologías"
          title="OKR es poderoso. EOS es completo."
          subtitle="OKR y EOS son metodologías que se respetan y pueden convivir. Pero si buscas transformar toda la forma de operar de tu empresa, EOS va más allá."
          withCta
        />

        {/* Comparison */}
        <section data-testid="okr-eos-comparison" className="section-padding bg-white">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">Dos enfoques, un objetivo</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                OKR y EOS: dos metodologías que se complementan.{" "}
                <span className="text-[#F26522]">Una es más completa.</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                Ambas metodologías tienen valor real. Aquí te mostramos en qué es poderosa cada una — y por qué EOS cubre más terreno.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
              {/* OKR card */}
              <div
                data-testid="okr-card"
                className="reveal rounded-2xl border border-slate-200 bg-slate-50/70 p-5 lg:p-7"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs font-bold tracking-[0.22em] uppercase text-slate-500">OKR</span>
                  <span className="font-display font-bold text-slate-300 text-lg">Objectives & Key Results</span>
                </div>
                <p className="mt-3 font-display font-extrabold text-2xl text-slate-700 leading-tight">
                  Excelente para definir y medir objetivos.
                </p>
                <ul className="mt-5 space-y-4">
                  {OKR_ITEMS.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex w-6 h-6 rounded-md bg-slate-200 items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.4} />
                      </span>
                      <div>
                        <p className="font-semibold text-slate-700 text-sm">{item.title}</p>
                        <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">{item.text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* EOS card */}
              <div
                data-testid="eos-card"
                className="reveal rounded-2xl border border-[#F26522]/20 bg-[#FEF3EB] p-5 lg:p-7"
                style={{ transitionDelay: "120ms" }}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#F26522]">EOS</span>
                  <span className="font-display font-bold text-navy-800/40 text-lg">Entrepreneurial Operating System</span>
                </div>
                <p className="mt-3 font-display font-extrabold text-2xl text-navy-800 leading-tight">
                  Ordena el sistema completo de tu empresa.
                </p>
                <ul className="mt-5 space-y-4">
                  {EOS_ITEMS.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-navy-700">
                      <span className="mt-0.5 inline-flex w-6 h-6 rounded-md bg-[#F26522] items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.8} />
                      </span>
                      <div>
                        <p className="font-semibold text-navy-800 text-sm">{item.title}</p>
                        <p className="text-sm text-navy-700/70 mt-0.5 leading-relaxed">{item.text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Por qué EOS va más allá */}
        <section data-testid="eos-differentiators" className="section-padding bg-slate-50/60">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">La ventaja competitiva de EOS</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                EOS va más allá del objetivo.{" "}
                <span className="text-[#F26522]">Transforma la forma de operar.</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                Tres razones por las que empresas medianas en Chile eligen EOS como su metodología de gestión principal.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {DIFFERENTIATORS.map((d, idx) => (
                <article
                  key={idx}
                  data-testid={`differentiator-card-${idx + 1}`}
                  className="reveal relative rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-[#F26522]/40 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  style={{ transitionDelay: `${idx * 90}ms` }}
                >
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#F26522] to-[#F26522]/40" />
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-navy-800 flex items-center justify-center shadow-md">
                      <d.icon className="w-5 h-5 text-white" strokeWidth={1.7} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26522]">
                      {d.num} / 03
                    </span>
                  </div>
                  <h3 className="mt-6 font-display font-extrabold text-2xl text-navy-800">{d.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{d.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <WhyCarlos />
        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
